import type { PriceMapCell, UpsertPriceRuleRequest } from "@planerius/contracts/cabinet"

import type { PriceRuleView } from "../../../api/prices.api"

/**
 * ДИНАМИЧЕСКИЕ ЦЕНЫ (ТЗ 03, Б10/Б11; макет «Каталог и клиенты - П2») — правила без вёрстки.
 *
 * Главное, что экран обязан объяснить: применяется ОДНО правило — самое приоритетное, при
 * равном приоритете более узкое; скидки не складываются. Цены считает сервер (тем же выбором,
 * что касса), здесь — только подписи, недели и проверки формы.
 */

export const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"] as const

export const SCOPE_LABEL: Readonly<Record<PriceRuleView["scope"], string>> = {
  service: "Услуга",
  category: "Категория",
  employee: "Все услуги мастера",
  branch: "Весь филиал",
}

/** Понедельник недели, в которую попадает дата. */
export function mondayOf(date: string): string {
  const at = new Date(`${date}T12:00:00Z`)
  const shift = (at.getUTCDay() + 6) % 7
  at.setUTCDate(at.getUTCDate() - shift)
  return at.toISOString().slice(0, 10)
}

export function addDays(date: string, days: number): string {
  const at = new Date(`${date}T12:00:00Z`)
  at.setUTCDate(at.getUTCDate() + days)
  return at.toISOString().slice(0, 10)
}

/** «Вт 14» — день недели и число. */
export function dayLabel(date: string): string {
  const at = new Date(`${date}T12:00:00Z`)
  return `${WEEKDAYS[(at.getUTCDay() + 6) % 7]} ${at.getUTCDate()}`
}

/** «1 440» — цена в ячейке без копеек и знака рубля: ячейка 26 пикселей. */
export function shortRub(minor: number): string {
  // Разряды — неразрывным пробелом: «1 440» не должно разорваться в узкой ячейке. Не Intl —
  // он ставит узкий пробел U+202F, который в части шрифтов виден как пустота.
  return String(Math.round(minor / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
}

export function rub(minor: number): string {
  return `${shortRub(minor)} ₽`
}

export type CellTone = "closed" | "same" | "cheaper" | "dearer"

export function toneOf(cell: PriceMapCell, baseMinor: number): CellTone {
  if (cell.priceMinor === null) return "closed"
  if (cell.priceMinor < baseMinor) return "cheaper"
  if (cell.priceMinor > baseMinor) return "dearer"
  return "same"
}

/** Подсказка ячейки: «1 440 ₽ · правило «Утро вторника −20 %» · базовая 1 800 ₽». */
export function cellTitle(cell: PriceMapCell, baseMinor: number): string {
  if (cell.priceMinor === null) return "Филиал закрыт или мастер не работает — цены нет, это не 0 ₽"
  if (cell.rule === null) return `Как в прайсе: ${rub(baseMinor)}`
  return `${rub(cell.priceMinor)} · правило «${cell.rule.name ?? "без имени"}» · базовая ${rub(baseMinor)}`
}

/** «пн–пт», «сб–вс», «каждый день» или перечисление. */
export function daysLabel(mask: number): string {
  if (mask === 127) return "каждый день"
  if (mask === 31) return "пн–пт"
  if (mask === 96) return "сб–вс"
  return WEEKDAYS.filter((_, i) => (mask & (1 << i)) !== 0)
    .map((day) => day.toLowerCase())
    .join(", ")
}

/** «−20 %», «+10 %», «цена 1 500 ₽». Минус — настоящий, а не дефис. */
export function effectLabel(rule: Pick<PriceRuleView, "mode" | "percent" | "fixedPriceMinor">): string {
  if (rule.mode === "fixed") return `цена ${rub(rule.fixedPriceMinor ?? 0)}`
  const value = rule.percent ?? 0
  return `${value < 0 ? "−" : "+"}${Math.abs(value)} %`
}

/** Строка правила в списке: «Стрижки · вт · 10:00–12:00 · −20 %». */
export function ruleSummary(rule: PriceRuleView, targetName: string): string {
  return [
    targetName,
    daysLabel(rule.weekdayMask),
    rule.timeFrom === null ? "весь день" : `${rule.timeFrom}–${rule.timeTo ?? ""}`,
    rule.dateFrom === null && rule.dateTo === null
      ? null
      : `${rule.dateFrom === null ? "" : `с ${rule.dateFrom}`}${rule.dateTo === null ? "" : ` по ${rule.dateTo}`}`.trim(),
    effectLabel(rule),
  ]
    .filter((part): part is string => part !== null)
    .join(" · ")
}

/**
 * Готовые шаблоны (ТЗ Б10, «Пусто»): они показывают жанр. «Вечер пятницы и суббота»
 * — два правила: одно окно времени на разные дни одним правилом не выражается.
 */
export function templates(branchId: string): { readonly label: string; readonly rules: UpsertPriceRuleRequest[] }[] {
  const base = { scope: "branch" as const, targetId: branchId, dateFrom: null, dateTo: null, fixedPriceMinor: null, isActive: true }
  return [
    {
      label: "Счастливые часы: будни до 12:00 дешевле на 20 %",
      rules: [
        { ...base, name: "Счастливые часы: будни до 12:00 −20 %", weekdayMask: 31, timeFrom: "00:00", timeTo: "12:00", mode: "percent", percent: -20, priority: 10 },
      ],
    },
    {
      label: "Вечер пятницы и суббота дороже на 10 %",
      rules: [
        { ...base, name: "Вечер пятницы +10 %", weekdayMask: 16, timeFrom: "17:00", timeTo: "23:59", mode: "percent", percent: 10, priority: 10 },
        { ...base, name: "Суббота +10 %", weekdayMask: 32, timeFrom: null, timeTo: null, mode: "percent", percent: 10, priority: 10 },
      ],
    },
  ]
}

export interface RuleForm {
  readonly name: string
  readonly scope: PriceRuleView["scope"]
  readonly targetId: string
  readonly weekdayMask: number
  readonly timeFrom: string
  readonly timeTo: string
  readonly dateFrom: string
  readonly dateTo: string
  readonly mode: PriceRuleView["mode"]
  readonly value: string
  readonly priority: string
  readonly isActive: boolean
}

export function ruleFormOf(rule: PriceRuleView | null, defaults: { scope: PriceRuleView["scope"]; targetId: string }): RuleForm {
  if (rule === null) {
    return {
      name: "",
      scope: defaults.scope,
      targetId: defaults.targetId,
      weekdayMask: 31,
      timeFrom: "10:00",
      timeTo: "12:00",
      dateFrom: "",
      dateTo: "",
      mode: "percent",
      value: "-20",
      priority: "10",
      isActive: true,
    }
  }
  return {
    name: rule.name ?? "",
    scope: rule.scope,
    targetId: rule.targetId,
    weekdayMask: rule.weekdayMask,
    timeFrom: rule.timeFrom ?? "",
    timeTo: rule.timeTo ?? "",
    dateFrom: rule.dateFrom ?? "",
    dateTo: rule.dateTo ?? "",
    mode: rule.mode,
    value: rule.mode === "fixed" ? String((rule.fixedPriceMinor ?? 0) / 100) : String(rule.percent ?? 0),
    priority: String(rule.priority),
    isActive: rule.isActive,
  }
}

/** Ошибки формы — словами ТЗ. null — сохранять можно. */
export function ruleProblem(form: RuleForm): string | null {
  if (form.name.trim() === "") return "Назовите правило словами — «Утро вторника −20 %». Через полгода никто не вспомнит, что делает «Правило № 3»."
  if (form.targetId === "") return "Выберите, на что действует правило."
  if (form.weekdayMask === 0) return "Выберите хотя бы один день недели."
  if ((form.timeFrom === "") !== (form.timeTo === "")) return "Укажите обе границы времени или ни одной."
  if (form.timeFrom !== "" && form.timeFrom >= form.timeTo) {
    return form.timeFrom === form.timeTo
      ? "Укажите промежуток: например, с 10:00 до 12:00."
      : "Начало раньше конца: ночное окно через полночь задайте двумя правилами."
  }
  if (form.dateFrom !== "" && form.dateTo !== "" && form.dateFrom > form.dateTo) return "Дата начала позже даты окончания."
  const value = Number(form.value.replace(",", "."))
  if (form.value.trim() === "" || !Number.isFinite(value)) return "Укажите значение: процент или цену."
  if (form.mode === "percent") {
    if (!Number.isInteger(value)) return "Процент — целым числом: −20 или 15."
    if (value < -100) return "Скидка больше 100 % сделает услугу бесплатной с доплатой клиенту. Скорее всего, имелось в виду другое."
    if (value > 500) return "Наценка больше 500 % — скорее всего, опечатка."
  } else if (value < 0) {
    return "Цена не бывает отрицательной."
  }
  return null
}

/** Не ошибка, но стоит переспросить (ТЗ Б10). */
export function ruleWarnings(form: RuleForm, today: string, baseMinor: number | null): string[] {
  const out: string[] = []
  if (form.dateTo !== "" && form.dateTo < today) {
    out.push(`Правило действовало до ${form.dateTo} и уже не работает. Измените даты или оставьте его в истории.`)
  }
  const value = Number(form.value.replace(",", "."))
  if (form.mode === "fixed" && baseMinor !== null && value * 100 > baseMinor && /скидк/i.test(form.name)) {
    out.push("В названии «скидка», а цена выше базовой — проверьте, это частая опечатка.")
  }
  if (form.mode === "percent" && value > 0 && /скидк/i.test(form.name)) {
    out.push("В названии «скидка», а процент со знаком плюс — это наценка. Скидка пишется с минусом: −20.")
  }
  return out
}

export function requestOfRule(form: RuleForm, id: string | null): UpsertPriceRuleRequest {
  const value = Number(form.value.replace(",", "."))
  return {
    ...(id === null ? {} : { id }),
    name: form.name.trim(),
    scope: form.scope,
    targetId: form.targetId,
    weekdayMask: form.weekdayMask,
    timeFrom: form.timeFrom === "" ? null : form.timeFrom,
    timeTo: form.timeTo === "" ? null : form.timeTo,
    dateFrom: form.dateFrom === "" ? null : form.dateFrom,
    dateTo: form.dateTo === "" ? null : form.dateTo,
    mode: form.mode,
    percent: form.mode === "percent" ? Math.trunc(value) : null,
    fixedPriceMinor: form.mode === "fixed" ? Math.round(value * 100) : null,
    priority: Number(form.priority) || 0,
    isActive: form.isActive,
  }
}

/** Запрос включения или выключения правила целиком — сохранение переписывает правило. */
export function toggledRule(rule: PriceRuleView, isActive: boolean): UpsertPriceRuleRequest {
  return requestOfRule({ ...ruleFormOf(rule, { scope: rule.scope, targetId: rule.targetId }), isActive }, rule.id)
}

/** «У 12 уже записанных клиентов цена останется прежней». */
export function futureLine(count: number): string {
  if (count === 0) return "Правило подействует на новые записи."
  const mod10 = count % 10
  const mod100 = count % 100
  const clients = mod10 === 1 && mod100 !== 11 ? "клиента" : "клиентов"
  return `Правило подействует на новые записи. У ${count} уже записанных ${clients} цена останется прежней.`
}

/**
 * Момент для настенного времени филиала: «вторник 10:00 по Москве» → ISO-момент.
 * Смещение зоны берётся у Intl на этот самый момент — переходы на летнее время учтены.
 */
export function zonedInstant(date: string, time: string, timeZone: string): string {
  const [y, m, d] = date.split("-").map(Number)
  const [hh, mm] = time.split(":").map(Number)
  const guess = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0)
  const offset = (at: number): number => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date(at))
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0)
    return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute")) - at
  }
  const first = guess - offset(guess)
  return new Date(guess - offset(first)).toISOString()
}
